import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Grid } from '@mui/material';
import clsx from 'clsx';
import { pick } from 'lodash';

import { navSections, useRegisterSection } from 'components/OnePageTemplates/common';
import { useGetOnePageOperatingLabels } from 'components/OnePageTemplates/constants/onePageSectionLabels';
import { useDataContext } from 'components/OnePageTemplates/context';
import { checkAmountWithDollarSign } from 'components/OnePageTemplates/OnePageFirstStyle/utils/checkAmountWithDollarSign';

import { hasMeaningfulValues } from 'helpers/hasMeaningfulValues';

import { InfoCard } from '../components/InfoCard';

import styles from './AboutTheCompanySection.module.scss';

export const AboutTheCompanySection: React.FC = () => {
  const { data, isExtendedPreview } = useDataContext();
  const { t } = useTranslation();
  const onePageSectionLabels = useGetOnePageOperatingLabels();
  const registerNavSection = useRegisterSection(navSections.aboutCompany);

  const sectionData = pick(data, [
    'companyBriefDescription',
    'operatingIndustry',
    'lifeCycleStage',
    'registrationDate',
    'numberOfEmployees',
  ]);

  const isAnnualIncome = checkAmountWithDollarSign(data.annualIncome ?? '');

  if (!hasMeaningfulValues(sectionData) && !isAnnualIncome) {
    return null;
  }

  const hasCards = hasMeaningfulValues([data.numberOfEmployees]) && isAnnualIncome;
  const hasOperatingIndustryData = hasMeaningfulValues([
    data.operatingIndustry,
    data.lifeCycleStage,
    data.registrationDate,
  ]);

  return (
    <Box
      {...registerNavSection}
      className={clsx(styles.section, {
        [styles.short]: !isExtendedPreview,
      })}
    >
      <Box
        className={clsx(styles.wrapper, {
          [styles.shortBg]: !isExtendedPreview,
        })}
      >
        {data.companyBriefDescription && (
          <Box className={styles.aboutTheCompanyWrapper}>
            <Box component="h2" className={styles.title}>
              {t('aboutThe')}
              {t('onePageMapping.company')}
            </Box>
            <Box className={styles.aboutTheCompanyContent}>{data.companyBriefDescription}</Box>
          </Box>
        )}

        {isExtendedPreview && (hasOperatingIndustryData || hasCards) && (
          <Box className={styles.container}>
            <Grid container spacing={3} flexWrap="wrap">
              {hasOperatingIndustryData && (
                <Grid item xs={12} md={6}>
                  <Grid container spacing={4}>
                    {data.operatingIndustry && (
                      <Grid item xs={12}>
                        <InfoCard
                          value={onePageSectionLabels.operatingIndustry}
                          label={data.operatingIndustry}
                          variant="transparent"
                        />
                      </Grid>
                    )}
                    {data.lifeCycleStage && (
                      <Grid item xs={12}>
                        <InfoCard
                          value={onePageSectionLabels.lifeCycleStage}
                          label={t(`onePageMapping.lifecycleStages.${data.lifeCycleStage}`)}
                          variant="transparent"
                        />
                      </Grid>
                    )}
                    {data.registrationDate && (
                      <Grid item xs={12}>
                        <InfoCard
                          value={onePageSectionLabels.registrationDate}
                          label={data.registrationDate}
                          variant="transparent"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}

              {hasCards && (
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    {data.numberOfEmployees && (
                      <Grid item xs={hasOperatingIndustryData ? 12 : 6}>
                        <InfoCard
                          value={data.numberOfEmployees}
                          label={onePageSectionLabels.numberOfEmployees}
                          variant="blur"
                        />
                      </Grid>
                    )}
                    {isAnnualIncome && data.annualIncome && (
                      <Grid item xs={hasOperatingIndustryData ? 12 : 6}>
                        <InfoCard
                          value={data.annualIncome}
                          label={onePageSectionLabels.averageAnnualRevenue}
                          variant="blur"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};
